import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { type ReactNode, lazy, Suspense, useState, useEffect } from 'react';
import { GuestManagementShowcase } from './GuestManagementShowcase';
import { RsvpShowcase } from './RsvpShowcase';
import { SaveTheDateVisual } from './SaveTheDateSection';
import { scrollToSection, staggerContainer, fadeInUp, scaleIn, displayFont } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

const CustomisationShowcase = lazy(() =>
  import('./CustomisationShowcase').then(m => ({ default: m.CustomisationShowcase }))
);

const EnvelopeVisual = lazy(() =>
  import('./EnvelopeSealSection').then(m => ({ default: m.EnvelopeVisual }))
);

const customisationPlaceholder = <div className="w-full aspect-[700/520] rounded-2xl bg-background-card border border-border" />;
const envelopePlaceholder = <div className="w-full aspect-[4/3] rounded-2xl bg-background-card border border-border" />;

type HeadlineFeaturesStrings = Pick<
  HomepageStrings,
  | 'headline_features_badge'
  | 'headline_features_h2_pre'
  | 'headline_features_h2_highlight'
  | 'headline_features_body'
  | 'headline_feature_blocks'
>;

const EN_DEFAULTS: HeadlineFeaturesStrings = {
  headline_features_badge: 'Features',
  headline_features_h2_pre: 'Everything you need, ',
  headline_features_h2_highlight: "nothing you don't",
  headline_features_body: 'Every feature is included. No add-ons, no tiers, no per-guest fees.',
  headline_feature_blocks: [
    { heading_pre: 'Make it ', heading_highlight: 'yours', heading_post: '', copy: 'Every detail is in your hands. Add your names, dates, venues, schedule and photos in minutes with our simple wizard. Show or hide sections based on guest type, and update anything at any time. Your invite is live, so your guests always see the latest version.', cta: 'See How It Works' },
    { heading_pre: 'Your guest list, completely ', heading_highlight: 'sorted', heading_post: '', copy: "Wedsy is your guest management hub. See RSVPs in real time, track who's opened their invite, manage day and evening guests, handle plus ones and dietary requirements, and chase up anyone who hasn't replied. All from one dashboard.", cta: 'Create Your Invite' },
    { heading_pre: 'RSVPs and reminders, ', heading_highlight: 'handled', heading_post: '', copy: "Real-time RSVP tracking means you always know where you stand. Guests respond in one tap, dietary needs are collected automatically, and anyone who hasn't replied gets a gentle nudge straight from your dashboard.", cta: 'Create Your Invite' },
    { heading_pre: 'Save the Date, ', heading_highlight: 'included', heading_post: '', copy: "Start with a Save the Date — it's a simple toggle in your invite settings. When you're ready to reveal the full details, just switch it to your full invite. More sections unlock, your design stays exactly the same, and there's nothing more to pay.", cta: 'Create Your Invite' },
    { heading_pre: "A wax seal reveal they'll ", heading_highlight: 'never forget', heading_post: '', copy: 'Every invite opens with an animated wax-sealed envelope — a moment of anticipation before guests see a single word. Choose from 18 envelope colour styles, each with a matching wax seal, and add it to your invite in one click.', cta: 'Create Your Invite' },
  ],
};

function HeadlineFeatureBlock({
  heading,
  copy,
  ctaLabel,
  ctaHref,
  visual,
  reverse,
}: {
  heading: ReactNode;
  copy: string;
  ctaLabel: string;
  ctaHref: string;
  visual: ReactNode;
  reverse?: boolean;
}) {
  const isExternal = ctaHref.startsWith('mailto:');
  const isScroll = ctaHref.startsWith('#');

  return (
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}>
      {/* Visual */}
      <motion.div
        variants={scaleIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className={`min-w-0 ${reverse ? 'lg:[direction:ltr]' : ''}`}
      >
        {visual}
      </motion.div>

      {/* Text */}
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className={reverse ? 'lg:[direction:ltr]' : ''}
      >
        <motion.h3
          variants={fadeInUp}
          className="text-2xl md:text-3xl lg:text-4xl text-text-primary mb-4"
          style={displayFont}
        >
          {heading}
        </motion.h3>
        <motion.p variants={fadeInUp} className="text-text-secondary text-base md:text-lg mb-6 leading-relaxed">
          {copy}
        </motion.p>
        <motion.div variants={fadeInUp}>
          {isScroll ? (
            <a
              href={ctaHref}
              onClick={(e) => scrollToSection(e, ctaHref.slice(1))}
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            >
              {ctaLabel} <ArrowRight className="w-5 h-5" />
            </a>
          ) : isExternal ? (
            <a
              href={ctaHref}
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            >
              {ctaLabel} <ArrowRight className="w-5 h-5" />
            </a>
          ) : (
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
            >
              {ctaLabel} <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}

export function HeadlineFeaturesSection({
  showWeddingFeatures = true,
  strings,
}: {
  showWeddingFeatures?: boolean;
  strings?: HeadlineFeaturesStrings;
}) {
  const s = strings ?? EN_DEFAULTS;
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const [b0, b1, b2, b3, b4] = s.headline_feature_blocks;

  return (
    <section id="features" className="py-20 md:py-32 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 md:space-y-32">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            {s.headline_features_badge}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
            style={displayFont}
          >
            {s.headline_features_h2_pre}<span className="text-primary">{s.headline_features_h2_highlight}</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto"
          >
            {s.headline_features_body}
          </motion.p>
        </motion.div>

        <HeadlineFeatureBlock
          heading={<>{b0.heading_pre}<span className="text-primary">{b0.heading_highlight}</span>{b0.heading_post}</>}
          copy={b0.copy}
          ctaLabel={b0.cta}
          ctaHref="#how-it-works"
          visual={mounted ? <Suspense fallback={customisationPlaceholder}><CustomisationShowcase /></Suspense> : customisationPlaceholder}
        />
        <HeadlineFeatureBlock
          heading={<>{b1.heading_pre}<span className="text-primary">{b1.heading_highlight}</span>{b1.heading_post}</>}
          copy={b1.copy}
          ctaLabel={b1.cta}
          ctaHref="/create"
          visual={<GuestManagementShowcase />}
          reverse
        />
        <HeadlineFeatureBlock
          heading={<>{b2.heading_pre}<span className="text-primary">{b2.heading_highlight}</span>{b2.heading_post}</>}
          copy={b2.copy}
          ctaLabel={b2.cta}
          ctaHref="/create"
          visual={<RsvpShowcase />}
        />
        {showWeddingFeatures && b3 && (
          <>
            <div id="save-the-date" />
            <HeadlineFeatureBlock
              heading={<>{b3.heading_pre}<span className="text-primary">{b3.heading_highlight}</span>{b3.heading_post}</>}
              copy={b3.copy}
              ctaLabel={b3.cta}
              ctaHref="/create"
              visual={<SaveTheDateVisual />}
              reverse
            />
          </>
        )}
        {showWeddingFeatures && b4 && (
          <HeadlineFeatureBlock
            heading={<>{b4.heading_pre}<span className="text-primary">{b4.heading_highlight}</span>{b4.heading_post}</>}
            copy={b4.copy}
            ctaLabel={b4.cta}
            ctaHref="/create"
            visual={mounted ? <Suspense fallback={envelopePlaceholder}><EnvelopeVisual /></Suspense> : envelopePlaceholder}
          />
        )}
      </div>
    </section>
  );
}
